import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import env from '@/core/config/env';
import { API_ENDPOINTS } from './endpoints';

/**
 * Interface for queued requests during token refresh
 */
interface QueuedRequest {
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
}

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/**
 * Axios instance with base configuration
 */
const apiClient = axios.create({
    baseURL: env.API_URL,
    timeout: env.API_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Request interceptor - Add auth token to requests
 */
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('auth_token');

        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

/**
 * Response interceptor - Handle common errors & token refresh
 */
apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Handle 401 Unauthorized (Expired Token)
        // Skip for login requests to prevent refresh loops or unwanted redirects
        if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.url?.includes(API_ENDPOINTS.AUTH.LOGIN) &&
            !originalRequest.url?.includes(API_ENDPOINTS.AUTH.LOGOUT)
        ) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        return apiClient(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Silent token refresh
                const { data: apiResponse } = await axios.post(`${env.API_URL}${API_ENDPOINTS.AUTH.REFRESH}`, {
                    refresh_token: localStorage.getItem('refresh_token'),
                });

                if (apiResponse.success && apiResponse.data) {
                    const { access_token } = apiResponse.data;
                    localStorage.setItem('auth_token', access_token);

                    processQueue(null, access_token);

                    if (originalRequest.headers) {
                        originalRequest.headers.Authorization = `Bearer ${access_token}`;
                    }
                    return apiClient(originalRequest);
                } else {
                    throw new Error(apiResponse.message || apiResponse.error || 'Refresh failed');
                }
            } catch (refreshError) {
                processQueue(refreshError as AxiosError, null);
                localStorage.removeItem('auth_token');
                localStorage.removeItem('refresh_token');
                
                // Get the tenant slug from authStore if it exists
                const authStorageStr = localStorage.getItem('raabta-auth-storage');
                let slug = '';
                try {
                    if (authStorageStr) {
                        const parsed = JSON.parse(authStorageStr);
                        slug = parsed.state?.tenant?.slug || '';
                    }
                } catch(e) {}
                
                window.location.href = slug ? `/login?school_slug=${slug}` : '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Handle other common status codes
        if (error.response?.status === 403) {
            console.error('Access forbidden');
        }

        if (error.response?.status === 500) {
            console.error('Server error occurred');
        }

        return Promise.reject(error);
    }
);

export default apiClient;
